import axios from 'axios';

const service = axios.create({
  baseURL: process.env.VUE_APP_API_BASE,
  timeout: 60000,
});

// 响应拦截：提取 data，检查 code
service.interceptors.response.use(
  (response) => {
    const body = response.data;
    if (body.code !== 0) {
      return Promise.reject(new Error(body.message || '请求失败'));
    }
    return body.data;
  },
  (error) => {
    const message = error.response?.data?.message || error.message || '请求失败';
    console.error('API Error:', message);
    return Promise.reject(new Error(message));
  }
);

/**
 * AI搜索 — 传入关键词返回关联文档列表（含下载链接与文件类型）
 * POST /lexiang/ai/search
 * @param {string} query - 搜索关键词（最长1024字符）
 * @returns {Promise<{
 *   requestId: string,
 *   list: Array<{
 *     title: string,       // 文件标题
 *     content: string,     // 内容简介
 *     url: string,          // 乐享页面链接
 *     score: number|null,
 *     downloadUrl: string|null,  // 文件下载链接
 *     fileType: string|null      // 文件类型（docx/pdf/mp4等）
 *   }>
 * }>}
 */
export const aiSearch = (query) => {
  return service.post('/lexiang/ai/search', { query });
};

/**
 * AI问答（降级通道）— 传入问题一次性返回答案，支持多轮对话
 * POST /lexiang/ai/qa
 * 说明：v2 主路径已切换为流式接口 askStream，本接口保留用于
 * 降级（URL ?qa=aggregate 或流式连续失败 2 次自动回退）。
 * @param {Object} params
 * @param {string} params.query - 问题内容（最长1024字符）
 * @param {string} [params.sessionId] - 会话ID（首次不传，后续回传）
 * @param {string} [params.qaMode] - 模型（react- 开头，如 react-ds-v4-flash）
 * @returns {Promise<{
 *   requestId: string,
 *   content: string,            // AI回答
 *   answerSource: string,
 *   reasoningContent: string|null,
 *   sessionId: string,          // 会话ID（带回给下次请求）
 *   generatedQuestion: string,
 *   referenceDocs: Array<{ title: string, url: string, downloadUrl:string, fileType:string }>,  // 参考文档，多了downloadUrl、fileType参数，也要和【AI搜索】一样，可以在前台查看文件
 *   referenceChunks: Array<{ content: string, title: string, url: string, targetId: string, targetType: string }>
 * }>}
 */
export const aiQA = (params) => {
  return service.post('/lexiang/ai/qa', params);
};

/**
 * AI问答（流式）— SSE 打字机输出
 * POST /lexiang/ai/qa/stream
 * 注意：接口为 POST，浏览器 EventSource 仅支持 GET，必须用
 * fetch + ReadableStream 手动解析 SSE，且不能走 axios 拦截器
 * （拦截器会一次性消费响应体，无法读流）。
 * @param {Object} params
 * @param {string} params.query - 问题内容（最长1024字符）
 * @param {string} [params.sessionId] - 会话ID（首次不传，后续回传）
 * @param {string} [params.qaMode] - 模型（react- 开头，如 react-ds-v4-flash）
 * @param {boolean} [params.hideDocs] - 是否屏蔽乐享文档链接，默认 false；true 时正文链接降级为纯文本、
 *   referenceDocs 恒为空、referenceChunks 仅 title/content（SSO 售后技能页传 true）
 * @param {AbortSignal} [params.signal] - 停止生成时传入的 AbortController.signal
 * @param {(delta: string) => void} [params.onContent] - content 事件：增量文本
 * @param {(docs: Array, chunks: Array) => void} [params.onDocs] - docs 事件：参考文档与引用片段（到达即渲染）
 * @param {(content: string, threadId: string) => void} [params.onDone] - done 事件：完整回答与会话ID
 * @param {(message: string) => void} [params.onError] - error 事件：后端返回的错误提示
 * @returns {Promise<{ interrupted: boolean }>} resolve 表示流已结束；
 *   interrupted=true 表示 EOF 未收到 done/error（断流中断）；
 *   HTTP 非 200 时 reject（Error）
 */
export const askStream = async ({
  query,
  sessionId,
  qaMode,
  hideDocs,
  signal,
  onContent,
  onDocs,
  onDone,
  onError,
  endpoint = '/lexiang/ai/qa/stream',
}) => {
  const body = { query };
  if (sessionId) body.sessionId = sessionId;
  if (qaMode) body.qaMode = qaMode;
  if (hideDocs) body.hideDocs = true;

  const resp = await fetch(`${process.env.VUE_APP_API_BASE || ''}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!resp.ok) {
    let detail = '';
    try {
      const text = await resp.text();
      if (text) detail = text.slice(0, 200);
    } catch (e) {
      // 忽略错误响应体读取失败
    }
    throw new Error(`请求失败(HTTP ${resp.status})${detail ? `：${detail}` : ''}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let receivedDone = false;
  let receivedError = false;

  // 解析单个 SSE 块（以空行分隔），分发事件
  const handleBlock = (block) => {
    let dataLine = '';
    const lines = block.split('\n');
    for (const line of lines) {
      if (line.startsWith('data:')) {
        dataLine = line.slice(5).trim();
      }
    }
    if (!dataLine) return;
    let evt;
    try {
      evt = JSON.parse(dataLine);
    } catch (e) {
      console.warn('SSE 解析失败:', dataLine);
      return;
    }
    switch (evt && evt.type) {
      case 'content':
        if (evt.delta && typeof onContent === 'function') {
          onContent(evt.delta);
        }
        break;
      case 'docs':
        if (typeof onDocs === 'function') {
          onDocs(evt.referenceDocs || [], evt.referenceChunks || []);
        }
        break;
      case 'done':
        receivedDone = true;
        if (typeof onDone === 'function') {
          onDone(evt.content || '', evt.threadId || '');
        }
        break;
      case 'error':
        receivedError = true;
        if (typeof onError === 'function') {
          onError(evt.message || '回答出错');
        }
        break;
      default:
        break;
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    // 统一 \r\n → \n，按空行切块；末尾不完整块留到下一轮
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
    let sep;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      handleBlock(buffer.slice(0, sep));
      buffer = buffer.slice(sep + 2);
    }
  }
  // 流结束：处理最后一段可能无空行结尾的内容
  if (buffer.trim()) {
    handleBlock(buffer);
  }

  return { interrupted: !receivedDone && !receivedError };
};
